// TODO: clean up data-table and table folders
import * as ScrollArea from '@radix-ui/react-scroll-area';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  TableMeta,
  ColumnFilter as TanstackColumnFilter,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import pluralize from 'pluralize';
import { ComponentProps, ReactNode, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { cn } from '../../utils/cn';
import { tw } from '../../utils/tw';
import { Button, ButtonProps } from '../button/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../table/table';
import { ColumnFilter } from './data-table-column-filter';
import { DataTableFilterControls } from './data-table-filter-controls';
import { DataTableGlobalSearch } from './data-table-global-search';
import { TablePagination } from './data-table-pagination';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_NO_RESULTS_MESSAGE = 'No results';
export const PAGE_PARAM_NAME = 'p';
export const GLOBAL_FILTER_PARAM_NAME = 'q';
export const VENDOR_FILTER_PARAM_NAME = 'v';
export const CATEGORY_FILTER_PARAM_NAME = 'c';
export const START_DATE_FILTER_PARAM_NAME = 'sd';
export const END_DATE_FILTER_PARAM_NAME = 'ed';

export type BaseTableRow = {
  id: string;
};

export type SearchInputConfig =
  | {
      show: false;
    }
  | {
      show: true;
      placeholder?: string;
    };

type ColumnFilterConfig = {
  accessor: string;
  label: string;
  search: SearchInputConfig;
};

type TableFilterConfig =
  | {
      show: false;
    }
  | {
      show: true;
      columns?: ColumnFilterConfig[];
      globalSearchConfig: ComponentProps<typeof DataTableFilterControls>['globalSearchConfig'];
      enableDateRangeFilter: boolean;
    };

type ActionButtonConfig =
  | {
      isVisible: false;
    }
  | {
      isVisible: true;
      text: string;
      onClick: () => void;
      icon?: ReactNode;
      isLoading?: boolean;
      variant?: ButtonProps['variant'];
    };

type TableActionButtonConfig = {
  primary: ActionButtonConfig;
  secondary: ActionButtonConfig;
};

type FullWidthSearchConfig =
  | {
      show: false;
    }
  | {
      show: true;
      placeholder: string;
    };

type ScrollingAreaConfig =
  | {
      show: false;
    }
  | {
      show: true;
      classNameScrollingWrapper?: string;
    };

export type DataTableProps<TData extends BaseTableRow> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  data: TData[];
  onSelectRow?: (id: TData['id']) => void;
  filters?: TableFilterConfig;
  fullWidthSearchConfig?: FullWidthSearchConfig;
  scrollingAreaConfig?: ScrollingAreaConfig;
  showPagination?: boolean;
  tableActionButtonConfig?: TableActionButtonConfig;
  showSelectedRowCount?: boolean;
  meta?: TableMeta<TData>;
  initialSorting?: SortingState;
  initialColumnVisibility?: VisibilityState;
  pageSize?: number;
  noResultsMessage?: string;
  onDataSorted?: (data: TData[]) => void;
  onFilteredRowsChange?: (filteredData: TData[]) => void;
  className?: string;
  /**
   * Determines row color scheme based on parent background.
   * - false (default): Optimized for white backgrounds
   * - true: Optimized for gray backgrounds (e.g., modals)
   */
  isOnGrayBackground?: boolean;
};

/**
 * A reusable data table component that supports sorting, filtering, and pagination.
 *
 * @template TData - The type of data being displayed in the table. Must extend BaseTableRow.
 *
 * @param props - Component props
 * @param props.columns - Column definitions for the table
 * @param props.data - Array of data items to display in the table
 * @param props.onSelectRow - Callback function triggered when a row is selected
 * @param props.filters - Optional configuration for table filtering
 *   - When {show: false}, filtering UI is hidden
 *   - When {show: true}, can enable global search and/or column-specific filters
 *   - filters.enableGlobalFilter - When true, shows a global search input that filters across all columns
 *   - filters.columns - Array of column filter configs, each with:
 *     - accessor: Must match either the 'id' or 'accessorKey' property defined in the column definition. For example, if your column definition has `accessorKey: 'vendor'` or `id: 'vendor'`, the accessor should be 'vendor'
 *     - label: Display label for the filter dropdown
  @param props.fullWidthSearchConfig - Optional configuration for a full-width search input
 *   - When {show: false}, the full-width search input is hidden
 *   - When {show: true}, it displays a search input above the table with the following properties:
 *     - placeholder: The placeholder text for the search input
 * @param props.scrollingAreaConfig - Optional configuration for a scrolling area around the table
 *   - When {show: false}, no scrolling area is applied
 *   - When {show: true}, the table is wrapped in a `ScrollArea` component and can have an optional className:
 *     - classNameScrollingWrapper: Optional className for the scrolling wrapper
 * @param props.showPagination - Optional boolean to control pagination visibility. Defaults to true
 * @param props.tableActionButtonConfig - Optional configuration for action buttons
 *   - primary: Configuration for the primary action button
 *     - isVisible: Boolean to control button visibility
 *     - text: The text to display on the button
 *     - onClick: Callback function triggered when the button is clicked
 *     - icon?: ReactNode - Optional icon to display on the button
 *     - isLoading?: boolean - Optional loading state for the button
 *   - secondary: Configuration for the secondary action button
 * @param props.showSelectedRowCount - Optional boolean to show the count of selected rows. Defaults to false
 * @param props.meta - Optional table metadata object for additional table configuration
 * @param props.initialSorting - Optional initial sorting configuration for the table. Accepts an array of sorting objects, where each object has:
 *   - id: string - The column id to sort by (must match a column's id or accessorKey)
 *   - desc: boolean - Whether to sort in descending order (true) or ascending order (false)
 * For example: [{id: 'name', desc: false}, {id: 'age', desc: true}] would sort first by name ascending, then by age descending
 * See TanStack Table docs for more details: https://tanstack.com/table/latest/docs/guide/sorting#initial-sorting-state
 * @param props.onFilteredRowsChange - Optional callback that is called whenever the filtered rows change (due to global search, column filters, or data updates)
 * @param props.className - Optional className to apply to the table container
 * @param props.isOnGrayBackground - Optional boolean to optimize row colors for gray backgrounds (e.g., modals). Defaults to false (white background)
 *
 * @example
 * ```tsx
 * <DataTable
 *   columns={transactionColumnDefinition}
 *   data={transactions}
 *   onSelectRow={(id) => handleTransactionRowSelect(id)}
 *   filters={{
 *     show: true,
 *     columns: [
 *       { accessor: 'vendors', label: 'Vendors' },
 *       { accessor: 'categories', label: 'Categories' }
 *     ],
 *     enableGlobalFilter: true
 *   }}
 *   tableActionButtonConfig={{
 *     primary: {
 *       isVisible: true,
 *       text: "Add New",
 *       onClick: () => handleAddNew()
 *     },
 *     secondary: { isVisible: false }
 *   }}
 *   showSelectedRowCount={true}
 *   initialSorting={[{ id: 'money_out', desc: true }]}
 *   className="flex-1"
 *   meta={{
 *     rowHeight: 76,
 *   }}
 * />
 * ```
 */
export const DataTable = <TData extends BaseTableRow>({
  columns,
  data,
  onSelectRow,
  filters = { show: false },
  fullWidthSearchConfig = { show: false },
  scrollingAreaConfig = { show: false },
  showPagination = true,
  tableActionButtonConfig = {
    primary: { isVisible: false },
    secondary: { isVisible: false },
  },
  showSelectedRowCount = false,
  meta,
  initialSorting,
  initialColumnVisibility,
  pageSize = DEFAULT_PAGE_SIZE,
  noResultsMessage = DEFAULT_NO_RESULTS_MESSAGE,
  onDataSorted,
  onFilteredRowsChange,
  className,
  isOnGrayBackground = false,
}: DataTableProps<TData>) => {
  const [tableWidth, setTableWidth] = useState(0);

  const tableRef = useRef<HTMLTableElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get(PAGE_PARAM_NAME) || '1');
  const initialGlobalFilter = searchParams.get(GLOBAL_FILTER_PARAM_NAME) || '';
  const initialDescriptionFilter = searchParams.get(VENDOR_FILTER_PARAM_NAME) || '';
  const initialCategoryFilter = searchParams.get(CATEGORY_FILTER_PARAM_NAME) || '';
  const initialColumnFiltersFromParams: ColumnFiltersState = [];
  const initialStartDateFilter = searchParams.get(START_DATE_FILTER_PARAM_NAME) || '';
  const initialEndDateFilter = searchParams.get(END_DATE_FILTER_PARAM_NAME) || '';

  if (initialDescriptionFilter) {
    initialColumnFiltersFromParams.push({ id: 'vendor', value: JSON.parse(initialDescriptionFilter) });
  }

  if (initialCategoryFilter) {
    initialColumnFiltersFromParams.push({ id: 'category', value: JSON.parse(initialCategoryFilter) });
  }

  if (initialStartDateFilter && initialEndDateFilter) {
    initialColumnFiltersFromParams.push({
      id: 'fspDate',
      value: [JSON.parse(initialStartDateFilter), JSON.parse(initialEndDateFilter)],
    });
  }

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPage - 1,
    pageSize,
  });
  const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFiltersFromParams);
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);

      let newColumnFilters;

      if (typeof updater === 'function') {
        const newState = updater(columnFilters);

        newColumnFilters = newState;
      } else {
        newColumnFilters = updater;
      }

      clearNonSelectedFilters(newColumnFilters, searchParams, setSearchParams);

      newColumnFilters.forEach((cf) => {
        if (cf.id === 'vendor') {
          searchParams.set(VENDOR_FILTER_PARAM_NAME, JSON.stringify(cf.value));
          setSearchParams(searchParams);

          return;
        }

        if (cf.id === 'category') {
          searchParams.set(CATEGORY_FILTER_PARAM_NAME, JSON.stringify(cf.value));
          setSearchParams(searchParams);

          return;
        }

        if (isDateRangeFilter(cf)) {
          searchParams.set(START_DATE_FILTER_PARAM_NAME, JSON.stringify(cf.value[0]));
          searchParams.set(END_DATE_FILTER_PARAM_NAME, JSON.stringify(cf.value[1]));

          setSearchParams(searchParams);
        }
      });
    },
    onGlobalFilterChange: (updater) => {
      setGlobalFilter(updater);

      let newGlobalFilter;

      if (typeof updater === 'function') {
        const newState = updater(globalFilter);

        newGlobalFilter = newState;
      } else {
        newGlobalFilter = updater;
      }

      if (newGlobalFilter) {
        setPagination({
          pageIndex: 0,
          pageSize,
        });

        searchParams.set(GLOBAL_FILTER_PARAM_NAME, newGlobalFilter);
        setSearchParams(searchParams);
      } else {
        searchParams.set(PAGE_PARAM_NAME, String(pagination.pageIndex + 1));
        searchParams.delete(GLOBAL_FILTER_PARAM_NAME);

        setSearchParams(searchParams);
      }
    },
    onPaginationChange: (updater) => {
      setPagination(updater);

      let newPageIndex;

      if (typeof updater === 'function') {
        const newState = updater(pagination);

        newPageIndex = newState.pageIndex;
      } else {
        newPageIndex = updater.pageIndex;
      }

      searchParams.set(PAGE_PARAM_NAME, String(newPageIndex + 1));
      setSearchParams(searchParams);
    },
    onSortingChange: setSorting,
    autoResetPageIndex: false, // This is needed to prevent pagination from resetting when AI is enriching the entities
    // There are 10 default built-in global filter functions to choose from
    // https://tanstack.com/table/latest/docs/guide/global-filtering#global-filter-function
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
      columnFilters,
      pagination,
      sorting,
    },
    initialState: {
      // TODO: Refactor initial state to use the new initialState property
      columnVisibility: initialColumnVisibility,
    },
    meta,
  });

  const shouldDisplayFilterControlsBlock = filters.show || tableActionButtonConfig.primary.isVisible;
  const shouldDisplayBottomBlock = showPagination || showSelectedRowCount;

  useEffect(() => {
    const div = tableRef.current;

    if (!div) return;

    const { width } = div.getBoundingClientRect();

    setTableWidth(width);
  }, []);

  useEffect(() => {
    if (initialPage > table.getPageCount()) {
      setPagination({
        pageIndex: 0,
        pageSize,
      });

      searchParams.set(PAGE_PARAM_NAME, '1');
      setSearchParams(searchParams);
    }
  }, [initialPage, pageSize, searchParams, setSearchParams, table]);

  useEffect(() => {
    if (!onDataSorted) {
      return;
    }

    const sortedRows = table.getRowModel().rows.map((row) => row.original);

    onDataSorted(sortedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  useEffect(() => {
    if (!onFilteredRowsChange) {
      return;
    }

    const filteredRows = table.getFilteredRowModel().rows.map((row) => row.original);

    onFilteredRowsChange(filteredRows);
  }, [onFilteredRowsChange, table, globalFilter, columnFilters]);

  return (
    <div className={cn('flex flex-col gap-5 py-5', className)}>
      {shouldDisplayFilterControlsBlock && (
        <div className="flex w-full flex-wrap items-center gap-4 px-4">
          {filters.show && (
            <DataTableFilterControls
              table={table}
              globalSearchConfig={filters.globalSearchConfig}
              withDateRangeFilter={filters.enableDateRangeFilter}
            >
              {filters.columns?.map(({ accessor, label, search }) => {
                const column = table.getColumn(accessor);

                if (!column) {
                  // TODO: when FE exception logging is implemented (Sentry?) we should log this to be
                  // alerted

                  // For now, we would see the error in the console and the app is not crashing
                  return null;
                }

                return <ColumnFilter key={accessor} column={column} label={label} search={search} />;
              })}
            </DataTableFilterControls>
          )}

          {tableActionButtonConfig.secondary.isVisible && (
            <Button
              variant="secondary"
              onClick={tableActionButtonConfig.secondary.onClick}
              iconLeft={tableActionButtonConfig.secondary.icon}
              isLoading={tableActionButtonConfig.secondary.isLoading}
              className="text-muted-foreground xl:ml-auto"
            >
              {tableActionButtonConfig.secondary.text}
            </Button>
          )}

          {tableActionButtonConfig.primary.isVisible && (
            <Button
              variant={tableActionButtonConfig.primary.variant}
              onClick={tableActionButtonConfig.primary.onClick}
              iconLeft={tableActionButtonConfig.primary.icon}
              isLoading={tableActionButtonConfig.primary.isLoading}
            >
              {tableActionButtonConfig.primary.text}
            </Button>
          )}
        </div>
      )}

      {fullWidthSearchConfig.show && (
        <DataTableGlobalSearch table={table} placeholder={fullWidthSearchConfig.placeholder} className="w-full" />
      )}

      <TableWrapper scrollingAreaConfig={scrollingAreaConfig}>
        <Table ref={tableRef}>
          <TableHeader className="text-xs text-muted-foreground">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const sumSize = table.getTotalSize();
                  const cellSize = header.column.getSize();
                  const cellWidthPercentage = cellSize / sumSize;
                  const finalWidth = cellWidthPercentage * tableWidth;

                  return (
                    // pl-5 allows the bg color to extend to the left
                    <TableHead
                      key={header.id}
                      className={header.column.getIndex() === 0 ? 'pl-5' : undefined}
                      style={{
                        width: `${finalWidth}px`,
                      }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody className="text-sm text-black">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, index) => {
                // TODO: We should move the isSelected state to the table and  use the row.getIsSelected() method instead of meta?.selectedRowId
                const isSelected = meta?.selectedRowId === row.original.id;
                const isEvenRow = index % 2 === 0;

                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={cn(
                      tw`cursor-pointer px-5`,
                      // Background colors based on parent background
                      isEvenRow
                        ? tw`bg-muted/50` // Darker for even rows (1st, 3rd, 5th...)
                        : isOnGrayBackground
                          ? tw`bg-white` // Lighter for odd rows on gray backgrounds
                          : tw`bg-white/80`, // Lighter for odd rows on white backgrounds
                      // Selected state
                      isSelected ? tw`bg-accent` : tw``,
                      {
                        [tw`cursor-auto`]: !onSelectRow && !meta?.setSelectedRowIds,
                        // Hover states - consistent across all rows
                        [tw`hover:bg-accent`]: !onSelectRow || meta?.setSelectedRowIds,
                      },
                    )}
                    style={meta?.rowHeight ? { height: `${meta.rowHeight}px` } : undefined}
                    onClick={(e) => {
                      // Only handle multi-select if setSelectedRowIds and selectedRowIds are present
                      if (meta?.setSelectedRowIds && meta?.selectedRowIds) {
                        // Prevent toggling if the click originated from a checkbox
                        if ((e.target as HTMLElement).closest('input[type="checkbox"]')) return;
                        const id = row.original.id;
                        const selected = meta.selectedRowIds;
                        if (selected.includes(id)) {
                          meta.setSelectedRowIds(selected.filter((sid) => sid !== id));
                        } else {
                          meta.setSelectedRowIds([...selected, id]);
                        }
                        return;
                      }
                      // Fallback to single-select logic
                      onSelectRow?.(row.original.id);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const sumSize = table.getTotalSize();
                      const cellSize = cell.column.getSize();
                      const cellWidthPercentage = cellSize / sumSize;
                      const finalWidth = cellWidthPercentage * tableWidth;

                      return (
                        // pl-5 allows the bg color to extend to the left
                        <TableCell
                          key={cell.id}
                          className={cn(
                            meta?.rowHeight ? `h-[${meta.rowHeight}px] overflow-hidden` : '',
                            cell.column.getIndex() === 0 ? 'pl-5' : undefined,
                          )}
                          style={{
                            width: `${finalWidth}px`,
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <p className="text-sm text-muted-foreground">{noResultsMessage}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {shouldDisplayBottomBlock && (
        <div className="flex items-center justify-between px-5">
          <div className="shrink-0">
            {table.getRowCount() > 0 && showSelectedRowCount && (
              <p className="text-xs font-medium">
                {`${table.getFilteredSelectedRowModel().rows.length} of ${table.getFilteredRowModel().rows.length} ${pluralize('row', table.getFilteredSelectedRowModel().rows.length)} selected`}
              </p>
            )}
          </div>

          {showPagination && <TablePagination table={table} />}
        </div>
      )}
    </div>
  );
};

const isDateRangeFilter = (
  columnFilter: TanstackColumnFilter,
): columnFilter is { id: 'fspDate'; value: [string, string] } => {
  return columnFilter.id === 'fspDate';
};

const clearNonSelectedFilters = (
  columnFilters: ColumnFiltersState,
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams) => void,
) => {
  const existingFilters = columnFilters.map((cf) => cf.id);

  if (!existingFilters.includes('vendor')) {
    searchParams.delete(VENDOR_FILTER_PARAM_NAME);
  }

  if (!existingFilters.includes('category')) {
    searchParams.delete(CATEGORY_FILTER_PARAM_NAME);
  }

  if (!existingFilters.includes('fspDate')) {
    searchParams.delete(START_DATE_FILTER_PARAM_NAME);
    searchParams.delete(END_DATE_FILTER_PARAM_NAME);
  }

  setSearchParams(searchParams);
};

type TableWrapperProps = {
  scrollingAreaConfig: ScrollingAreaConfig;
  children: ReactNode;
};

const TableWrapper = ({ scrollingAreaConfig, children }: TableWrapperProps) => {
  if (!scrollingAreaConfig.show) {
    return children;
  }

  return (
    <ScrollArea.Root className={cn('overflow-y-hidden', scrollingAreaConfig.classNameScrollingWrapper)}>
      <ScrollArea.Viewport className="size-full rounded">{children}</ScrollArea.Viewport>

      <ScrollArea.Scrollbar
        className="flex touch-none bg-muted p-0.5 transition-colors duration-160 ease-out select-none hover:bg-muted data-[orientation=horizontal]:h-2.5 data-[orientation=horizontal]:flex-col data-[orientation=vertical]:w-2.5"
        orientation="vertical"
      >
        <ScrollArea.Thumb className="relative flex-1 rounded-lg bg-muted-foreground before:absolute before:top-1/2 before:left-1/2 before:size-full before:min-h-11 before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2" />
      </ScrollArea.Scrollbar>

      <ScrollArea.Corner className="bg-blackA5" />
    </ScrollArea.Root>
  );
};
